import { z } from 'zod';

/**
 * Project-level reports — one round-trip returns every metric the
 * Reports tab needs.  Each branch is independent (FE can render them in
 * parallel) but they share the same `[from, to]` window so the eye can
 * correlate across charts.
 *
 *   - Burndown:        open vs done count for each day in window.
 *   - Throughput:      tasks closed per week (week starts Mon, project tz).
 *   - Lead time:       distribution stats (avg / p50 / p90) over tasks
 *                      completed in the window.  Just numbers — the chart
 *                      is a stat card row, not a fan-out viz.
 *   - Status breakdown:current count per status (donut chart).  Not
 *                      window-scoped — it's a snapshot.
 *
 * The window is closed on `from` and open on `to` (`>=from, <to`) — the
 * usual "give me this period" interpretation.  Server clamps the window
 * to 365 days max so a careless query can't pull years of history.
 */
export const ProjectReportsQuerySchema = z.object({
  /** ISO yyyy-mm-dd in the project's timezone. Defaults to 30 days ago. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** Exclusive. Defaults to today + 1 day in the project's timezone. */
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** Comma-separated uuids — narrow charts to tasks where assignee_id IN (...). */
  assigneeIds: z.string().optional(),
  /** Single uuid — narrow charts to tasks in this milestone. */
  milestoneId: z.string().uuid().optional(),
});
export type ProjectReportsQuery = z.infer<typeof ProjectReportsQuerySchema>;

const BurndownPointSchema = z.object({
  date: z.string(),
  openCount: z.number().int().nonnegative(),
  doneCount: z.number().int().nonnegative(),
});
export type BurndownPoint = z.infer<typeof BurndownPointSchema>;

const ThroughputPointSchema = z.object({
  /** Monday of the week, yyyy-mm-dd in project tz. */
  weekStart: z.string(),
  closedCount: z.number().int().nonnegative(),
});
export type ThroughputPoint = z.infer<typeof ThroughputPointSchema>;

const LeadTimeStatsSchema = z.object({
  /** Number of tasks completed inside the window. */
  count: z.number().int().nonnegative(),
  /** All values in fractional days. `null` when count === 0. */
  avgDays: z.number().nullable(),
  p50Days: z.number().nullable(),
  p90Days: z.number().nullable(),
});
export type LeadTimeStats = z.infer<typeof LeadTimeStatsSchema>;

const StatusBreakdownItemSchema = z.object({
  statusId: z.string(),
  statusName: z.string(),
  color: z.string(),
  category: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']),
  count: z.number().int().nonnegative(),
});
export type StatusBreakdownItem = z.infer<typeof StatusBreakdownItemSchema>;

export const ProjectReportsSchema = z.object({
  /** Echo back so the FE can guard against stale responses while filters change. */
  from: z.string(),
  to: z.string(),
  burndown: z.array(BurndownPointSchema),
  throughput: z.array(ThroughputPointSchema),
  leadTime: LeadTimeStatsSchema,
  statusBreakdown: z.array(StatusBreakdownItemSchema),
});
export type ProjectReports = z.infer<typeof ProjectReportsSchema>;

// ── W5-0 · Reports lite (mobile Reports tab, Overview sparkline) ────────────

/**
 * Burn-up point derived from a burndown point: `scope` = tasks that existed by
 * the end of the day (open + done), `done` = done by the end of the day.
 */
export interface BurnUpPoint {
  date: string;
  scope: number;
  done: number;
}

/** Burndown → burn-up, same days, no extra request. */
export function burnUpFromBurndown(points: readonly BurndownPoint[]): BurnUpPoint[] {
  return points.map((p) => ({ date: p.date, scope: p.openCount + p.doneCount, done: p.doneCount }));
}

/**
 * GET /api/teams/:teamSlug/projects/:projectSlug/workload?days=1..365 (default 30)
 * — one row per assignee, most loaded first. Gated by `time.view_team` (Lead+;
 * everyone else gets 403 and the card hides). Task counts are a live
 * snapshot; the `*LastWindow` time totals cover the last `days` days. Public
 * task and time-entry facts only (P11).
 */
export const ProjectWorkloadRowSchema = z.object({
  userId: z.string().uuid(),
  name: z.string(),
  username: z.string(),
  avatarUrl: z.string().nullable(),
  tasksAssigned: z.number().int().nonnegative(),
  tasksInProgress: z.number().int().nonnegative(),
  tasksCompleted: z.number().int().nonnegative(),
  tasksOverdue: z.number().int().nonnegative(),
  /** Σ estimates (labor hours) of the assigned tasks. */
  plannedHours: z.number().nonnegative(),
  /** Σ hours spent on the assigned tasks. */
  loggedHours: z.number().nonnegative(),
  /** Seconds logged with the timer in the window. */
  timerSecondsLastWindow: z.number().int().nonnegative(),
  /** Seconds logged manually in the window. */
  manualSecondsLastWindow: z.number().int().nonnegative(),
  /** Time entries in the window. */
  entriesLastWindow: z.number().int().nonnegative(),
});
export type ProjectWorkloadRowProjection = z.infer<typeof ProjectWorkloadRowSchema>;

export const ProjectWorkloadSchema = z.array(ProjectWorkloadRowSchema);
export type ProjectWorkloadProjection = z.infer<typeof ProjectWorkloadSchema>;
